import React, { useState, useEffect, useCallback, useMemo } from 'react';

// Onsite Pending — paste a list of identifiers (employee IDs, badge barcodes,
// logins, or names; comma- or newline-separated) into each of 8 reusable
// entries, and see which of them are currently ONSITE for the selected
// date/shift (cross-referenced against the Roll Call roster + onsite data).
//
// The 8 lists + their labels are GLOBAL and reusable (saved once, re-checked
// against whatever date/shift is selected). Stored in ONSITE_PENDING / data.

const NUM_ENTRIES = 8;

function norm(s) { return (s || '').trim().toLowerCase(); }

export default function OnsitePending({ api, site }) {
  const [date, setDate] = useState('');
  const [shift, setShift] = useState('day');
  const [labels, setLabels] = useState(Array(NUM_ENTRIES).fill('').map((_, i) => `Entry ${i + 1}`));
  const [lists, setLists] = useState(Array(NUM_ENTRIES).fill(''));  // raw pasted text per entry
  const [roster, setRoster] = useState(null);     // {bbToPerson, empToBb, ...}
  const [onsite, setOnsite] = useState(null);      // {onsite:[{empId,...}]}
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    const pst = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const h = pst.getHours();
    const d = new Date(pst);
    if (h < 6) d.setDate(d.getDate() - 1);
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    setShift(h >= 8 && h < 19 ? 'day' : 'night');
  }, []);

  // Load the saved global lists + labels.
  useEffect(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=ONSITE_PENDING&shift_type=data`)
      .then(r => r.json())
      .then(d => {
        if (Array.isArray(d?.lists)) setLists(d.lists.slice(0, NUM_ENTRIES).concat(Array(NUM_ENTRIES).fill('')).slice(0, NUM_ENTRIES));
        if (Array.isArray(d?.labels)) setLabels(d.labels.slice(0, NUM_ENTRIES).map((l, i) => l || `Entry ${i + 1}`).concat(Array(NUM_ENTRIES).fill('')).slice(0, NUM_ENTRIES).map((l, i) => l || `Entry ${i + 1}`));
        setDirty(false);
      })
      .catch(() => {});
  }, [api, site]);

  const loadRoster = useCallback(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=ROSTER_${site}&shift_type=data`)
      .then(r => r.json()).then(d => setRoster(d && d.bbToPerson ? d : null)).catch(() => setRoster(null));
  }, [api, site]);
  const loadOnsite = useCallback(() => {
    if (!date) return;
    fetch(`${api}/api/fclm/cache?site=${site}&date=ONSITE_${site}_${date}&shift_type=${shift}`)
      .then(r => r.json()).then(d => setOnsite(d && Array.isArray(d.onsite) ? d : null)).catch(() => setOnsite(null));
  }, [api, site, date, shift]);
  useEffect(() => { loadRoster(); loadOnsite(); }, [loadRoster, loadOnsite]);
  useEffect(() => { const t = setInterval(loadOnsite, 30000); return () => clearInterval(t); }, [loadOnsite]);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${api}/api/fclm/cache`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site, date: 'ONSITE_PENDING', shift_type: 'data', data: { lists, labels } }),
      });
      setDirty(false); setStatus('Saved ✓'); setTimeout(() => setStatus(''), 2500);
    } catch (e) { setStatus('Save failed'); } finally { setSaving(false); }
  };

  // Build onsite lookup: a Set of every identifier (empId, bb, login, name) that
  // maps to an onsite associate, each -> the resolved person.
  const onsiteIndex = useMemo(() => {
    const idx = {};   // normalized token -> {name, empId, login, bb}
    const bbToPerson = roster?.bbToPerson || {};
    const empToPerson = {};
    Object.values(bbToPerson).forEach(p => { empToPerson[p.empId] = p; });
    const onsiteEmp = new Set((onsite?.onsite || []).map(o => o.empId));
    // onsite attendance rows also carry name; keep as fallback for name matches.
    const onsiteByEmp = {};
    (onsite?.onsite || []).forEach(o => { onsiteByEmp[o.empId] = o; });
    onsiteEmp.forEach(emp => {
      const p = empToPerson[emp] || onsiteByEmp[emp] || { empId: emp };
      const name = p.name || onsiteByEmp[emp]?.name || '';
      const login = p.login || '';
      const bb = p.bb || '';
      const person = { name, empId: emp, login, bb };
      [emp, login, bb, name].forEach(tok => { if (tok) idx[norm(tok)] = person; });
    });
    return idx;
  }, [roster, onsite]);

  const parseTokens = (raw) => (raw || '').split(/[\n,]+/).map(t => t.trim()).filter(Boolean);

  const results = useMemo(() => lists.map(raw => {
    const toks = parseTokens(raw);
    const onsiteHits = []; const notOnsite = [];
    const seen = new Set();
    toks.forEach(tok => {
      const hit = onsiteIndex[norm(tok)];
      if (hit) { const key = hit.empId; if (!seen.has(key)) { seen.add(key); onsiteHits.push({ tok, ...hit }); } }
      else notOnsite.push(tok);
    });
    return { total: toks.length, onsiteHits, notOnsite };
  }), [lists, onsiteIndex]);

  const setList = (i, v) => { setLists(prev => prev.map((x, j) => j === i ? v : x)); setDirty(true); };
  const setLabel = (i, v) => { setLabels(prev => prev.map((x, j) => j === i ? v : x)); setDirty(true); };

  const card = { border: '1px solid #ccc', borderRadius: 4, padding: 8, minWidth: 230, flex: '1 1 230px', background: '#fff' };
  const taStyle = { width: '100%', minHeight: 90, fontSize: '0.72rem', fontFamily: 'inherit', border: '1px solid #ddd', borderRadius: 3, padding: 4, resize: 'vertical' };

  return (
    <div>
      <h2 className="section-title">Onsite Pending — {site}</h2>
      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <label style={{ fontSize: '0.75rem' }}>Date: <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ fontSize: '0.75rem' }} /></label>
        <label style={{ fontSize: '0.75rem' }}>Shift:{' '}
          <select value={shift} onChange={e => setShift(e.target.value)} style={{ fontSize: '0.75rem' }}>
            <option value="day">Day</option><option value="night">Night</option>
          </select>
        </label>
        <button onClick={save} disabled={saving} className="btn btn-primary" style={{ fontSize: '0.72rem', padding: '4px 12px', background: dirty ? '#f39c12' : undefined }}>
          {saving ? 'Saving…' : dirty ? '💾 Save *' : '💾 Save'}
        </button>
        <span style={{ fontSize: '0.7rem', color: '#444' }}>
          Checking against onsite roster for <strong>{date} {shift}</strong>{onsite?.onsite ? ` (${onsite.onsite.length} onsite)` : ' — no roster yet'}.
        </span>
        {!roster && <span style={{ fontSize: '0.7rem', color: '#c0392b' }}>⚠ Roster not loaded — only exact employee-ID matches will resolve.</span>}
        {status && <span style={{ fontSize: '0.72rem', color: '#27ae60' }}>{status}</span>}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {lists.map((raw, i) => {
          const r = results[i];
          return (
            <div key={i} className="card" style={card}>
              <input value={labels[i]} onChange={e => setLabel(i, e.target.value)}
                style={{ width: '100%', fontWeight: 700, fontSize: '0.8rem', border: 'none', borderBottom: '2px solid #232f3e', marginBottom: 6, padding: '2px 0' }} />
              {/* Onsite results */}
              <div style={{ fontSize: '0.72rem', marginBottom: 6 }}>
                <div style={{ fontWeight: 600, color: '#27ae60' }}>Onsite ({r.onsiteHits.length}/{r.total})</div>
                {r.onsiteHits.length === 0 ? <div style={{ color: '#888' }}>— none onsite —</div> : (
                  <ul style={{ margin: '2px 0', paddingLeft: 16 }}>
                    {r.onsiteHits.map((h, j) => <li key={j} style={{ color: '#1b5e20' }}>{h.name || h.empId}{h.name ? '' : ''} <span style={{ color: '#999' }}>({h.tok})</span></li>)}
                  </ul>
                )}
                {r.notOnsite.length > 0 && (
                  <details style={{ marginTop: 2 }}>
                    <summary style={{ color: '#c0392b', cursor: 'pointer' }}>Not onsite / unmatched ({r.notOnsite.length})</summary>
                    <div style={{ color: '#999', whiteSpace: 'pre-wrap', fontSize: '0.68rem' }}>{r.notOnsite.join(', ')}</div>
                  </details>
                )}
              </div>
              {/* Total list paste box */}
              <div style={{ fontSize: '0.68rem', color: '#666', fontWeight: 600 }}>Total List (paste IDs / badges / logins / names)</div>
              <textarea value={raw} onChange={e => setList(i, e.target.value)} placeholder={'e.g.\nbobsmith, 11751854\njdoe'} style={taStyle} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
